import {customElement, property, query, state} from "lit/decorators.js";
import {html, type TemplateResult} from "lit";
import ICON_THUMBS_UP from "../../../../svgpictograms/thumbs_up.svg";
import ICON_THUMBS_DOWN from "../../../../svgpictograms/thumbs_down.svg";
import {resolve} from "../../container";
import {FeedbackService} from "./feedbackService";
import {schedule} from "../../common/utils/promises";
import {EopOverlaySpinner} from "../../page/elements/spinner";
import {Dictionary} from "../../page/elements/dictionary";
import {FORM_SUBMITTED_EVENT, type FormSubmittedEvent} from "../form/formEvents";
import {FeedbackStorage} from "./feebackStorage";
import {unsafeHTML} from "lit/directives/unsafe-html.js";
import {UnLitElement} from "../../common/elements";
import {fnv32Hash} from "../../common/crypto";
import {trackingButtonInteract} from "../../tracking/trackingAttributes";

export const FEEDBACK_NEGATIVE = "negative";
export const FEEDBACK_POSITIVE = "positive";

export type Reason = {
    category: string;
    label: string;
};

export interface FeedbackUi {
    setStage: (stage: FeedbackStage) => void;
    setFeedbackId: (feedbackId: string) => void;
}

export abstract class FeedbackStage {
    public abstract cssName: string;

    public submit(pageUrl: string, choice: string): void {
    }

    public submitQualifiedFeedback(pageUrl: string, success: boolean): void {
    }
}

export class AskForThumbFeedback extends FeedbackStage {
    public cssName: string = "ask-for-thumb-feedback";

    public constructor(private feedbackUi: FeedbackUi, private feedbackService: FeedbackService, private feedbackStorage: FeedbackStorage, private spinner: () => EopOverlaySpinner) {
        super();
    }

    public submit(pageUrl: string, choice?: string): void {
        void schedule(this.spinner().spinWhile(this.postFeedback(pageUrl, choice!)))
            .as("postFeedback");
    }

    private async postFeedback(pageUrl: string, choice: string): Promise<void> {
        const feedBackResponse = await this.feedbackService.postPageFeedback(pageUrl, choice);
        const feedbackId = feedBackResponse.id;
        this.feedbackStorage.saveFeedbackId(pageUrl, choice, feedBackResponse.id);
        this.feedbackUi.setFeedbackId(feedbackId);

        if (choice === FEEDBACK_POSITIVE) {
            if (this.feedbackStorage.isQualifiedFeedbackGivenForChoice(FEEDBACK_POSITIVE)) {
                this.feedbackUi.setStage(new ThanksForPositiveFeedback(this.feedbackStorage));
            } else {
                this.feedbackUi.setStage(new AskForNewsletterSubmission(this.feedbackUi, this.feedbackStorage));
            }
        } else if (choice === FEEDBACK_NEGATIVE) {
            this.feedbackUi.setStage(new AskForQualifiedNegativeFeedback(this.feedbackUi, this.feedbackStorage));
        }
    }
}

export class AskForNewsletterSubmission extends FeedbackStage {
    public cssName: string = "ask-for-newsletter-submission";

    public constructor(private feedbackUi: FeedbackUi, private feedbackStorage: FeedbackStorage) {
        super();
    }

    public submitQualifiedFeedback(pageUrl: string, success: boolean): void {
        if (success) {
            this.feedbackStorage.saveQualifiedFeedbackFor(pageUrl);
            this.feedbackUi.setStage(new ThanksForSubscribeToNewsLetter());
        } else {
            this.feedbackUi.setStage(new ErrorWhileNewsletterSubmission());
        }
    }

    public submit(pageUrl: string, choice?: string): void {
        this.feedbackStorage.saveQualifiedFeedbackFor(pageUrl);
        this.feedbackUi.setStage(new ThanksForSubscribeToNewsLetter());
    }
}

export class ThanksForPositiveFeedback extends FeedbackStage {
    public cssName: string = "thanks-for-positive-feedback";

    public constructor(private feedbackStorage: FeedbackStorage) {
        super();
    }

    public submit(pageUrl: string, choice?: string): void {
        this.feedbackStorage.saveQualifiedFeedbackFor(pageUrl);
    }
}

export class AskForQualifiedNegativeFeedback extends FeedbackStage {
    public cssName: string = "ask-for-qualified-negative-feedback";

    public constructor(private feedbackUi: FeedbackUi, private feedbackStorage: FeedbackStorage) {
        super();
    }

    public submitQualifiedFeedback(pageUrl: string, success: boolean): void {
        if (success) {
            this.feedbackStorage.saveQualifiedFeedbackFor(pageUrl);
            this.feedbackUi.setStage(new ThanksForQualifiedNegativeFeedback());
        } else {
            this.feedbackUi.setStage(new ErrorWhileQualifiedNegativeFeedback());
        }
    }
}

export class ErrorWhileQualifiedNegativeFeedback extends FeedbackStage {
    public cssName: string = "error-while-qualified-negative-feedback";
}

export class ErrorWhileNewsletterSubmission extends FeedbackStage {
    public cssName: string = "error-while-newsletter-submission";
}

export class ThanksForQualifiedNegativeFeedback extends FeedbackStage {
    public cssName: string = "thanks-for-qualified-negative-feedback";
}

export class ThanksForSubscribeToNewsLetter extends FeedbackStage {
    public cssName: string = "thanks-for-subscribe-to-newsletter";
}

export class AllDone extends FeedbackStage {
    public cssName: string = "all-done";
}

@customElement("eop-page-feedback")
export class EopPageFeedback extends UnLitElement implements FeedbackUi {

    @property({attribute: "page-url"})
    public pageUrl: string;
    @property({attribute: "question"})
    public question: string;

    @property({attribute: "reasons", type: Array})
    public reasons: Reason[] = [];

    @property({attribute: "stage", type: String, reflect: true})
    public stage: string = "";

    @state()
    public feedbackStage: FeedbackStage;
    @state()
    public feedbackId: string;

    @query("eop-overlay-spinner")
    protected fetchMoreSpinner: EopOverlaySpinner;

    private dictionary: Dictionary;

    private pageUrlHash: string;

    public constructor(
        private feedbackService: FeedbackService = resolve(FeedbackService),
        private feedbackStorage: FeedbackStorage = resolve(FeedbackStorage)
    ) {
        super();
    }

    public setStage(feedbackStage: FeedbackStage): void {
        this.feedbackStage = feedbackStage;
        this.stage = feedbackStage.cssName;
    }

    public setFeedbackId(feedbackId: string): void {
        this.feedbackId = feedbackId;
    }

    public connectedCallback(): void {
        super.connectedCallback();
        this.dictionary = Dictionary.of(this);

        this.addEventListener(FORM_SUBMITTED_EVENT, (e: Event) => {
            const formSubmittedEvent = e as FormSubmittedEvent;
            this.feedbackStage.submitQualifiedFeedback(this.pageUrl, formSubmittedEvent.detail.success);
        });

        this.pageUrlHash = fnv32Hash(this.pageUrl);

        this.restoreFromStorage();
    }

    private restoreFromStorage(): void {
        const feedbackId = this.feedbackStorage.getFeedbackIdFor(this.pageUrl);
        const qualifiedFeedbackGivenOnThisPage = this.feedbackStorage.isQualifiedFeedbackGivenFor(this.pageUrl);
        let feedbackStage;
        if (feedbackId) {
            this.feedbackId = feedbackId;
            if (qualifiedFeedbackGivenOnThisPage) {
                feedbackStage = new AllDone();
            } else {
                const positiveQualifiedFeedbackOnOtherPage = this.feedbackStorage.isQualifiedFeedbackGivenForChoice(FEEDBACK_POSITIVE);
                const choice = this.feedbackStorage.getFeedbackChoiceFor(this.pageUrl);
                if (choice === FEEDBACK_POSITIVE && positiveQualifiedFeedbackOnOtherPage) {
                    feedbackStage = new AllDone();
                } else if (choice === FEEDBACK_POSITIVE) {
                    feedbackStage = new AskForNewsletterSubmission(this, this.feedbackStorage);
                } else if (choice === FEEDBACK_NEGATIVE) {
                    feedbackStage = new AskForQualifiedNegativeFeedback(this, this.feedbackStorage);
                } else {
                    throw new Error(`Unknown feedback choice stored for page ${this.pageUrl}: ${choice}`);
                }
            }
        } else {
            feedbackStage = new AskForThumbFeedback(this, this.feedbackService, this.feedbackStorage, () => this.fetchMoreSpinner);
        }
        this.feedbackStage = feedbackStage;
        this.stage = this.feedbackStage.cssName;
    }

    public render(): TemplateResult {
        const feedbackQuestion = this.dictionary.translate("MSG_FEEDBACK_QUESTION");
        const thumbsUpLabel = this.dictionary.translate("MSG_THUMBS_UP_LABEL");
        const thumbsDownLabel = this.dictionary.translate("MSG_THUMBS_DOWN_LABEL");

        const negativeFeedbackFollowUpText = this.dictionary.translate("MSG_NEGATIVE_FEEDBACK_FOLLOW_UP_TEXT");
        const positiveFeedbackFollowUpText = this.dictionary.translate("MSG_POSITIVE_FEEDBACK_FOLLOW_UP_TEXT");
        const positiveFeedbackEndText = this.dictionary.translate("MSG_POSITIVE_FEEDBACK_END_TEXT");

        return html`
            <div class="thumbs">
                <eop-overlay-spinner></eop-overlay-spinner>
                <h3 class="question">${feedbackQuestion}</h3>
                <div class="feedback-options reverse-background">
                    <button class="thumbs-up"
                            ${trackingButtonInteract("page-feedback-thumbs-up")}
                            @click=${this.thumbUpClicked}
                            aria-label=${thumbsUpLabel}
                    >${unsafeHTML(ICON_THUMBS_UP)}</button>
                    <button class="thumbs-down"
                            ${trackingButtonInteract("page-feedback-thumbs-down")}
                            @click=${this.thumbDownClicked}
                            aria-label=${thumbsDownLabel}
                    >${unsafeHTML(ICON_THUMBS_DOWN)}</button>
                </div>
            </div>
            <div class="negative-feedback-follow-up reverse-background">
                <div class="thank-you-and-follow-up-text">${unsafeHTML(negativeFeedbackFollowUpText)}</div>
                ${this.renderNegativeFeedbackReasons()}
            </div>
            <div class="positive-feedback-follow-up reverse-background">
                <div class="thank-you-and-follow-up-text">${unsafeHTML(positiveFeedbackFollowUpText)}</div>
                ${this.renderPositiveNewsletterRegistration()}
            </div>
            <div class="positive-feedback-end reverse-background">
                ${unsafeHTML(positiveFeedbackEndText)}
            </div>
        `;
    }

    private thumbUpClicked(): void {
        this.feedbackStage.submit(this.pageUrl, FEEDBACK_POSITIVE);
    }

    private thumbDownClicked(): void {
        this.feedbackStage.submit(this.pageUrl, FEEDBACK_NEGATIVE);
    }

    private renderPositiveNewsletterRegistration(): TemplateResult {
        const buttonLabel = this.dictionary.translate("MSG_POSITIVE_NEWSLETTER_REGISTRATION_BUTTON");
        const submittedSuccess = this.dictionary.translate("MSG_POSITIVE_NEWSLETTER_REGISTRATION_SUCCESS");
        const submittedError = this.dictionary.translate("MSG_POSITIVE_NEWSLETTER_REGISTRATION_ERROR");
        const emailLabel = this.dictionary.translate("MSG_POSITIVE_NEWSLETTER_REGISTRATION_EMAIL_LABEL");
        const invalidEmailMessage = this.dictionary.translate("MSG_POSITIVE_NEWSLETTER_REGISTRATION_INVALID_EMAIL_MESSAGE");
        const requiredEmailMessage = this.dictionary.translate("MSG_POSITIVE_NEWSLETTER_REGISTRATION_REQUIRED_EMAIL_MESSAGE");
        const disclaimer = this.dictionary.translate("MSG_POSITIVE_NEWSLETTER_REGISTRATION_DISCLAIMER");
        const footerText = this.dictionary.translate("MSG_POSITIVE_NEWSLETTER_REGISTRATION_FOOTER_TEXT");

        const validation = [{
            "message": invalidEmailMessage,
            "warningLevel": "error",
            "validation": {"id": "email"}
        }, {
            "message": requiredEmailMessage,
            "warningLevel": "error",
            "validation": {"id": "required"}
        }];

        return html`
            <eop-form class="newsletter-registration-form"
                      id="positive-newsletter-registration-form-${this.pageUrlHash}"
                      submit-service-id="positiveNewsletterRegistrationService"
                      scroll-after-submit="none">
                <div slot="step-newsletter-registration">
                    <eop-form-step step-id="step-newsletter-registration" next-label=${buttonLabel}>
                        <eop-form-section section-id="newsletter">
                            <eop-form-text-field
                                    input-id="email"
                                    label=${emailLabel}
                                    validations=${JSON.stringify(validation)}></eop-form-text-field>
                        </eop-form-section>
                        <eop-form-section section-id="info-text">
                            <eop-form-conditional-element class="form-info-text form-element">${unsafeHTML(disclaimer)}</eop-form-conditional-element>
                        </eop-form-section>
                    </eop-form-step>
                </div>
                <div slot="success">
                    ${unsafeHTML(submittedSuccess)}
                </div>
                <div slot="error">
                    <p>${submittedError}</p>
                </div>
                <div slot="footer-text" class="form-footer-text">
                    <p>${footerText}</p>
                </div>
            </eop-form>
        `;
    }

    private renderNegativeFeedbackReasons(): TemplateResult {
        const buttonLabel = this.dictionary.translate("MSG_NEGATIVE_REASONS_SUBMIT_BUTTON");
        const submittedSuccess = this.dictionary.translate("MSG_NEGATIVE_REASONS_SUBMITTED_SUCCESS");
        const submittedError = this.dictionary.translate("MSG_NEGATIVE_REASONS_SUBMITTED_ERROR");

        return html`
            <eop-form class="negative-feedback-form"
                      id="negative-feedback-reasons-form-${this.pageUrlHash}"
                      submit-service-id="negativeReasonsFeedbackService"
                      scroll-after-submit="none">
                <div slot="step-reasons">
                    <eop-form-step step-id="step-reasons" next-label=${buttonLabel}>
                        <eop-form-section section-id="reasons">
                            <eop-form-hidden-field input-id="feedback_id" hidden-value=${this.feedbackId}></eop-form-hidden-field>
                            ${this.renderNegativeFeedbackReasonsCheckboxes()}
                        </eop-form-section>
                    </eop-form-step>
                </div>
                <div slot="success">
                    ${unsafeHTML(submittedSuccess)}
                </div>
                <div slot="error">
                    <p>${submittedError}</p>
                </div>
            </eop-form>
        `;
    }

    private renderNegativeFeedbackReasonsCheckboxes(): TemplateResult {
        const atLeastOneReasonMessage = this.dictionary.translate("MSG_NEGATIVE_REASONS_AT_LEAST_ONE_REASON_MESSAGE");
        const validation = [{
            "message": atLeastOneReasonMessage,
            "warningLevel": "error",
            "validation": {
                "id": "minCheckedOptions",
                "count": 1
            }
        }];
        const options = this.reasons.map(reason => {
            return {
                id: reason.category,
                label: reason.label,
                preselected: false,
                uncheckedLabel: "",
                checkedLabel: ""
            };
        });
        return html`
            <eop-form-checkbox-group
                    input-id="options"
                    options=${JSON.stringify(options)}
                    validations=${JSON.stringify(validation)}>
            </eop-form-checkbox-group>`;
    }
}